import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiCheckbox,
  MiDatePicker,
  MiDetailFields,
  MiInputSelectBox,
  MiModal,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  ATTEMPT_STATUS_COLORS,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CALENDAR_TYPES,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
  SUBMIT_STATUSES,
  SUBMIT_STATUS_COLORS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  addToBusinessDate,
  getDate,
  toFormattedDate,
  toSnappedTime,
} from "@miplatform/utils";
import {
  mdiCalendarRemove,
  mdiCancel,
  mdiCheck,
  mdiClose,
  mdiHandFrontLeftOutline,
  mdiLockOutline,
  mdiOctagon,
  mdiPlay,
  mdiRayStartArrow,
} from "@mdi/js";
import { Box } from "@mui/material";
import { useTaskCardActionModal } from "./useTaskActionModalViewModel";

const HOLD_UNITS = ["Hours", "Days"];

/**
 * Global modal for actions on TaskCardModal
 *
 * @param {Object} props - The TaskCardModal props object
 * @param {Object} props.MENU_LABELS - Map that includes all the possible actions on a task
 * @param {string} props.openModal - The currently open modal type
 * @param {Object} props.toastService - The toast service instance
 * @param {function} props.onAttemptUpdate - Function that will be trigger on attempt update
 * @param {function} props.closeModal - Function that will close the open modal
 * @param {Object} props.attempt - Task object
 *
 * @returns
 */
const TaskActionModal = ({
  MENU_LABELS,
  openModal,
  onAttemptUpdated,
  onEmailModalOpen,
  closeModal,
  updateCalendarCounts,
  attempt,
  calendarSet = {},
}) => {
  const modalEdit = useEdit({
    contactName: "",
    reason: "",
    customReason: "",
    overrideHoldDuration: false,
    holdUnit: "Hours",
    lockTypes: {
      user: true,
      date: true,
      time: true,
    },
  });

  const {
    isLoading,
    cancelReasons,
    handleStartWork,
    handleDispatchWork,
    handleUnschedule,
    handleStop,
    handleSubmitWork,
    handleHold,
    handleLock,
    loadCancelReasons,
    handleCancel,
  } = useTaskCardActionModal({
    toastService,
    onAttemptUpdated,
    onEmailModalOpen,
    closeModal,
    attempt,
  });
  const holdCalendars = (calendarSet.calendarConfigs || [])
    .filter((x) => x.calendarType === CALENDAR_TYPES.HOLDS_LIST)
    .map((x) => {
      return { label: x.name, value: x.id, ...x };
    });
  const { classes, theme } = useStyles();
  const selectedCalendar = holdCalendars.find(
    (x) => x.id === modalEdit.getValue("calendarId")
  );
  const followUpDefaultOffsetHours =
    selectedCalendar?.followUpDefaultOffsetHours || null;

  useEffect(() => {
    modalEdit.reset();
  }, [openModal]);

  const getDefaultHoldDateTimeUpdates = () => {
    if (!selectedCalendar || !followUpDefaultOffsetHours) {
      return {
        followUpDate: null,
        followUpTime: null,
        holdValue: null,
      };
    }
    const followUpDateTime = addToBusinessDate(
      getDate(),
      followUpDefaultOffsetHours,
      "hours"
    );
    return {
      followUpDate: followUpDateTime,
      followUpTime: toFormattedDate(
        toSnappedTime(followUpDateTime),
        DATE_TIME_FORMATS.FORMAT_24_HOUR
      ),
      holdValue: followUpDefaultOffsetHours,
    };
  };

  useEffect(() => {
    modalEdit.update(getDefaultHoldDateTimeUpdates());
  }, [modalEdit.getValue("calendarId")]);

  const handleCloseModal = () => {
    closeModal();
    modalEdit.reset();
  };

  const handleLockTypeChange = (type) => (e) => {
    modalEdit.update({
      lockTypes: {
        ...modalEdit.getValue("lockTypes"),
        [type]: e.target.checked,
      },
    });
  };

  const holdDetailFields = [
    {
      label: "Contact/Customer Name",
      name: "contactName",
      required: true,
      width: "100%",
      style: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    {
      label: "Hold Type",
      name: "calendarId",
      fieldType: INPUT_TYPES.SELECT,
      style: { paddingLeft: 0, paddingRight: 0 },
      width: "100%",
      options: holdCalendars,
      required: true,
    },
    {
      label: "Reason",
      name: "reason",
      width: "100%",
      required: true,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      label: "Hold Unit",
      name: "holdUnit",
      fieldType: INPUT_TYPES.SELECT,
      className: classes.orangeInputBox,
      options: HOLD_UNITS.map((unit) => ({
        label: unit,
        value: unit,
      })),
      handleChange: (e) => {
        const holdUnit = e.target.value;
        const updatedDateTime = addToBusinessDate(
          getDate(),
          modalEdit.getValue("holdValue"),
          holdUnit
        );

        modalEdit.update({
          holdUnit,
          followUpDate: updatedDateTime,
          followUpTime: toFormattedDate(
            toSnappedTime(updatedDateTime),
            DATE_TIME_FORMATS.FORMAT_24_HOUR
          ),
        });
      },
      style: {
        paddingLeft: 0,
        paddingRight: theme.spacing(0.5),
      },
    },
    {
      label: "Hold Value",
      name: "holdValue",
      fieldType: INPUT_TYPES.NUMERIC,
      allowNegative: false,
      thousandSeparator: true,
      allowLeadingZeros: false,
      decimalScale: 0,
      handleChange: ({ event: e = {} }) => {
        const holdValue = e.target?.value;
        const updatedDateTime = addToBusinessDate(
          getDate(),
          holdValue,
          modalEdit.getValue("holdUnit")
        );

        modalEdit.update({
          holdValue,
          followUpDate: updatedDateTime,
          followUpTime: toFormattedDate(
            toSnappedTime(updatedDateTime),
            DATE_TIME_FORMATS.FORMAT_24_HOUR
          ),
        });
      },
      style: {
        paddingLeft: theme.spacing(2),
        paddingRight: 0,
      },
      inputTextboxProps: {
        className: classes.orangeInputBox,
      },
    },
    {
      label: "Follow-Up Date",
      name: "followUpDate",
      fieldType: INPUT_TYPES.CUSTOM,
      style: {
        paddingLeft: 0,
        paddingTop: theme.spacing(2),
        paddingRight: theme.spacing(1.2),
      },
      customComponent: <MiDatePicker />,
      disabled: true,
    },
    {
      label: "Follow-Up Time",
      name: "followUpTime",
      fieldType: INPUT_TYPES.TIME,
      style: {
        paddingLeft: theme.spacing(1),
        paddingRight: 0,
        paddingTop: theme.spacing(1),
      },
      sx: {
        "& .MuiInputLabel-root": {
          left: theme.spacing(1),
        },
      },
      disabled: true,
    },
    {
      label: "Override default hold duration",
      name: "overrideHoldDuration",
      fieldType: INPUT_TYPES.CHECKBOX,
      handleChange: ({ target: { checked } }) => {
        if (checked) {
          modalEdit.update({
            overrideHoldDuration: checked,
            holdUnit: "Hours",
          });
        } else {
          modalEdit.update({
            overrideHoldDuration: checked,
            holdUnit: "Hours",
            ...getDefaultHoldDateTimeUpdates(),
          });
        }
      },
      width: "100%",
      style: { padding: theme.spacing(0.5, 0) },
    },
  ];

  const actionsConfig = {
    [MENU_LABELS.START]: {
      submitAction: {
        name: "Start",
        onClick: () => handleStartWork({ onAttemptUpdated }),
        disabled: isLoading,
      },
      title: "Start Work",
      titleIcon: mdiPlay,
      leftIconColor: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.STARTED],
      description: "Do you want to start this work?",
    },
    [MENU_LABELS.DISPATCH]: {
      submitAction: {
        name: "Dispatch",
        onClick: handleDispatchWork,
        disabled: isLoading,
      },
      title: "Dispatch Work",
      titleIcon: mdiRayStartArrow,
      leftIconColor: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.DISPATCHED],
      description: "Do you want to dispatch this work?",
    },
    [MENU_LABELS.STOP]: {
      submitAction: {
        name: "Stop Work",
        onClick: handleStop,
        disabled: isLoading,
      },
      title: "Stop Work",
      titleIcon: mdiOctagon,
      leftIconColor: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.PLANNED],
      description: "Do you want to stop this work?",
    },
    [MENU_LABELS.UNSCHEDULE]: {
      submitAction: {
        name: "Unschedule Work",
        onClick: handleUnschedule,
        disabled: isLoading,
      },
      title: "Unschedule Work",
      titleIcon: mdiCalendarRemove,
      leftIconColor: theme.palette.red.main,
      description: "Do you want to unschedule this work?",
    },
    [MENU_LABELS.LOCK]: {
      submitAction: {
        name: "Lock Order",
        onClick: () => handleLock(modalEdit.getValue("lockTypes")),
        disabled: !Object.values(modalEdit.getValue("lockTypes")).some(Boolean),
      },
      title: "Lock Order",
      titleIcon: mdiLockOutline,
      leftIconColor: theme.palette.grey.dark,
      description:
        "Once an order is locked it will require a manager approval to unlock.",
      extraComponent: (
        <Box className={classes.checkBoxContainer}>
          <MiCheckbox
            size="small"
            style={{ padding: theme.spacing(1) }}
            label={`Locked to ${attempt.assignedUserName}`}
            value={modalEdit.getValue("lockTypes").user}
            handleChange={handleLockTypeChange("user")}
          />
          <MiCheckbox
            size="small"
            style={{ padding: theme.spacing(1) }}
            label={`Locked to Day (${toFormattedDate(
              attempt.displayStartTime,
              DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY
            )})`}
            value={modalEdit.getValue("lockTypes").date}
            handleChange={handleLockTypeChange("date")}
          />
          <MiCheckbox
            size="small"
            style={{ padding: theme.spacing(1) }}
            label={`Locked to Time (${toFormattedDate(
              attempt.displayStartTime,
              DATE_TIME_FORMATS.FORMAT_12_HOUR
            )})`}
            value={modalEdit.getValue("lockTypes").time}
            handleChange={handleLockTypeChange("time")}
          />
        </Box>
      ),
    },
    [MENU_LABELS.JOB_COMPLETE]: {
      submitAction: {
        name: "Job Complete",
        onClick: () =>
          handleSubmitWork({
            submitStatus: SUBMIT_STATUSES.COMPLETE,
          }),
        disabled: isLoading,
      },
      title: "Job Complete",
      titleIcon: mdiCheck,
      leftIconColor: SUBMIT_STATUS_COLORS[SUBMIT_STATUSES.COMPLETE],
      description: "Mark this job as complete?",
    },
    [MENU_LABELS.JOB_INCOMPLETE]: {
      submitAction: {
        name: "Job Incomplete",
        onClick: () =>
          handleSubmitWork({
            submitStatus: SUBMIT_STATUSES.INCOMPLETE,
          }),
        disabled: isLoading,
      },
      title: "Job Incomplete",
      titleIcon: mdiClose,
      leftIconColor: SUBMIT_STATUS_COLORS[SUBMIT_STATUSES.INCOMPLETE],
      description: "Mark this job as incomplete?",
    },
    [MENU_LABELS.HOLD]: {
      submitAction: {
        name: "Hold Order",
        onClick: () =>
          handleHold({
            ...modalEdit.getValue(),
            followUpDefaultOffsetHours,
            updateCalendarCounts,
          }),
        disabled:
          isLoading ||
          !modalEdit.allFilled("contactName", "calendarId", "reason"),
      },
      extraComponent: (
        <Box key="hold-modal-form" component="form">
          <MiDetailFields
            edit={modalEdit}
            detailFields={
              modalEdit.getValue("overrideHoldDuration")
                ? [
                    ...holdDetailFields.slice(0, 3),
                    holdDetailFields.at(-1),
                    ...holdDetailFields.slice(3, -1),
                  ]
                : holdDetailFields.filter(
                    ({ name }) => !["holdUnit", "holdValue"].includes(name)
                  )
            }
          />
        </Box>
      ),
      title: "Hold Order",
      titleIcon: mdiHandFrontLeftOutline,
      leftIconColor: theme.palette.purple.dark,
      description:
        "Holding an order will automatically place everything related to the order on hold.",
    },
    [MENU_LABELS.CANCEL]: {
      title: "Cancel Order",
      titleIcon: mdiCancel,
      leftIconColor: theme.palette.error.main,
      description:
        Number(attempt.attemptNumber?.replace("*", "")) > 1
          ? "You are cancelling a re-attempt order. This will remove the re-attempt order and all the tasks associated with it. Are you sure you want to continue? Please provide a reason to cancel it."
          : "Please provide a reason to cancel this order and all the tasks associated with it.",
      fetchOnOpen: loadCancelReasons,
      extraComponent: (
        <MiInputSelectBox
          key="reason-select-box"
          labelText="Reason"
          options={cancelReasons.map((x) => ({
            label: x.reason,
            value: x.reason,
          }))}
          handleChange={(e) => {
            modalEdit.update({ reason: e.target.value });
          }}
          value={modalEdit.getValue("reason")}
          size="small"
          disableNone
        />
      ),
      submitAction: {
        name: "Cancel Order",
        onClick: () =>
          handleCancel({
            reason: modalEdit.getValue("reason"),
          }),
        disabled: !modalEdit.getValue("reason"),
      },
    },
  };

  useEffect(() => {
    if (
      openModal &&
      actionsConfig[openModal] &&
      actionsConfig[openModal].fetchOnOpen
    ) {
      actionsConfig[openModal].fetchOnOpen();
    } else {
      modalEdit.reset();
    }
  }, [openModal]);

  return (
    actionsConfig[openModal] && (
      <MiModal
        open
        closeModal={handleCloseModal}
        actions={[
          {
            name: "Cancel",
            onClick: handleCloseModal,
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
          },
          {
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.PRIMARY,
            ...actionsConfig[openModal].submitAction,
          },
        ]}
        title={actionsConfig[openModal].title}
        titleIcon={actionsConfig[openModal].titleIcon}
        leftIconColor={actionsConfig[openModal].leftIconColor}
        description={actionsConfig[openModal].description}
        width={actionsConfig[openModal].width ?? 448}
        loading={isLoading}
      >
        {actionsConfig[openModal].extraComponent}
      </MiModal>
    )
  );
};

const useStyles = makeStyles()((theme) => ({
  orangeInputBox: {
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.orange.light,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.dark,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
  },
  checkBoxContainer: {
    display: "flex",
    flexDirection: "column",
  },
}));

export default TaskActionModal;
